/**
 * Self-Registry-Loader fuer das Frontend.
 *
 * Laedt einmal beim App-Start `/api/portal/modules-registry` und stellt
 * die Modul-Liste (slug, name, icon, default_acl_level, version, capabilities)
 * als React-Context zur Verfuegung. Komponenten nutzen useModuleRegistry().
 *
 * Zusaetzlich werden die Labels in den globalen `moduleLabels`-Helper gespiegelt,
 * damit auch nicht-React-Code (z.B. format-Funktionen) sie nutzen kann.
 *
 * Joerg 2026-06-12, Plug-in-Architektur Etappe 0.
 */
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, useAuth } from '@onvice/portal-shell-api';

import { setLiveModuleLabels } from '../lib/moduleLabels';

export interface ModuleRegistryEntry {
  slug: string;
  name: string;
  icon: string;
  default_acl_level: 'admin' | 'editor' | 'author' | 'tester' | 'reader' | 'depositor' | 'none';
  version: string;
  container_url: string | null;
  capabilities: string[];
  managed_in_admin: boolean;
  last_seen_at: string;
}

interface ModuleRegistryContextValue {
  modules: ModuleRegistryEntry[];
  bySlug: Record<string, ModuleRegistryEntry>;
  labelFor: (slug: string) => string;
  isLoaded: boolean;
}

const ModuleRegistryContext = createContext<ModuleRegistryContextValue>({
  modules: [],
  bySlug: {},
  labelFor: (slug) => slug,
  isLoaded: false,
});

export function ModuleRegistryProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const [modules, setModules] = useState<ModuleRegistryEntry[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    // Joerg 2026-06-12: Registry-Fetch nur fuer eingeloggte User. Ohne diesen
    // Guard ruft der Provider /portal/modules-registry sofort beim App-Mount —
    // ohne Cookie kommt 401 → apiFetch faellt auf window.location='/login'
    // → App remountet → Loop. Reproduzierbar als „46 Frame-Navigationen in 8s"
    // im Tester.
    if (!isAuthenticated) {
      setIsLoaded(true);
      return;
    }
    void api.get<{ success: boolean; data: ModuleRegistryEntry[] }>('/portal/modules-registry')
      .then((r) => {
        if (r.success && Array.isArray(r.data)) {
          setModules(r.data);
          const labels: Record<string, string> = {};
          for (const m of r.data) labels[m.slug] = m.name;
          setLiveModuleLabels(labels);
        }
      })
      .catch(() => { /* fail-soft, Fallback in moduleLabels greift */ })
      .finally(() => setIsLoaded(true));
  }, [isAuthenticated]);

  const value = useMemo<ModuleRegistryContextValue>(() => {
    const bySlug: Record<string, ModuleRegistryEntry> = {};
    for (const m of modules) bySlug[m.slug] = m;
    return {
      modules,
      bySlug,
      labelFor: (slug) => bySlug[slug]?.name ?? slug,
      isLoaded,
    };
  }, [modules, isLoaded]);

  return <ModuleRegistryContext.Provider value={value}>{children}</ModuleRegistryContext.Provider>;
}

export function useModuleRegistry(): ModuleRegistryContextValue {
  return useContext(ModuleRegistryContext);
}
